import type { AddOn, SelectedAddOn } from './types';

export const MAX_ADD_ON_QUANTITY = 99;

export function setAddOnQuantity(
  selections: SelectedAddOn[],
  addOn: AddOn,
  quantity: number,
): SelectedAddOn[] {
  const boundedQuantity = Number.isFinite(quantity)
    ? Math.min(MAX_ADD_ON_QUANTITY, Math.max(0, Math.trunc(quantity)))
    : 0;
  const remaining = selections.filter((selection) => selection.addOn.id !== addOn.id);
  if (boundedQuantity === 0) return remaining;

  const existingIndex = selections.findIndex((selection) => selection.addOn.id === addOn.id);
  const updated = { addOn, quantity: boundedQuantity };
  if (existingIndex < 0) return [...remaining, updated];

  const insertionIndex = selections
    .slice(0, existingIndex)
    .filter((selection) => selection.addOn.id !== addOn.id).length;
  return [...remaining.slice(0, insertionIndex), updated, ...remaining.slice(insertionIndex)];
}

export function incrementAddOnQuantity(selections: SelectedAddOn[], addOn: AddOn) {
  const currentQuantity = selections
    .filter((selection) => selection.addOn.id === addOn.id)
    .reduce((total, selection) => total + (selection.quantity ?? 1), 0);
  return setAddOnQuantity(selections, addOn, currentQuantity + 1);
}

/** Merge legacy or malformed duplicate selections into one capped selection per add-on. */
export function normalizeSelectedAddOns(selections: SelectedAddOn[] = []): SelectedAddOn[] {
  return selections.reduce<SelectedAddOn[]>((normalized, selection) => {
    const currentQuantity = normalized
      .filter((current) => current.addOn.id === selection.addOn.id)
      .reduce((total, current) => total + (current.quantity ?? 1), 0);
    return setAddOnQuantity(
      normalized,
      selection.addOn,
      currentQuantity + (selection.quantity ?? 1),
    );
  }, []);
}
